"use client"
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Header } from "./header";
import { AnnouncementBanner } from "./announcement-banner";
import { ImpersonationBanner } from "./impersonation-banner";
import { ProfileCompletionBanner } from "./profile-completion-banner";
import { SubscriptionAlertBanner } from "./subscription-alert-banner";
import { UsageLimitBanner } from "./usage-limit-banner";
import { Sidebar } from "./sidebar";
import { TreePine } from "lucide-react";

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64); // Default height
  const [isMounted, setIsMounted] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768); // md breakpoint
        if (window.innerWidth >= 768) {
          setSidebarOpen(false); // Close mobile sidebar when screen becomes larger
        }
      }
    };

    checkScreenSize();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, [isMounted]);

  // Measure header height and update CSS variable
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current && typeof document !== 'undefined') {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeaderHeight();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateHeaderHeight);
    }

    // Use ResizeObserver for more accurate measurements
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeaderHeight) : null;
    if (headerRef.current && resizeObserver) {
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateHeaderHeight);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (typeof document !== 'undefined') {
        document.documentElement.style.removeProperty('--header-height');
      }
    };
  }, [isMounted]);

  const toggleSidebar = () => {
    if (isMobile) {
      // On mobile: toggle the overlay sidebar
      setSidebarOpen(!sidebarOpen);
    } else {
      // On desktop: trigger sidebar collapse/expand via event
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('toggle-sidebar');
        window.dispatchEvent(event);
      }
    }
  };

  // Prevent hydration mismatch by not rendering until mounted
  if (!isMounted) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 shadow-forest">
            <TreePine className="h-6 w-6 text-primary animate-pulse" />
          </div>
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-muted/20 text-foreground overflow-hidden">
      <div className="flex h-full">
        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="hidden md:fixed md:left-0 md:top-0 md:h-full md:z-20 md:block">
          <Sidebar />
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobile && (
          <>
            {/* Backdrop */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/50 z-40 md:hidden"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            {/* Mobile Sidebar — matches forest gradient */}
            <div className={`
              fixed left-0 top-0 h-full z-50 transform transition-transform duration-300 md:hidden
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
              <Sidebar />
            </div>
          </>
        )}

        {/* Main Content Area */}
        <div className={`
          flex-1 flex flex-col h-full transition-all duration-300 w-[80%]
          ${isMobile ? 'ml-0' : 'md:ml-[var(--sidebar-width,16rem)]'}
        `}>
          {/* Fixed Header */}
          <div
            ref={headerRef}
            className={`
              fixed top-0 right-0 z-30 transition-all duration-300
              ${isMobile ? 'left-0' : 'md:left-[var(--sidebar-width,16rem)]'}
            `}
          >
            <ImpersonationBanner />
            <AnnouncementBanner />
            <Header onMenuClick={toggleSidebar} showMenuButton={true} />
            <ProfileCompletionBanner />
            <SubscriptionAlertBanner />
            <UsageLimitBanner />
          </div>

          {/* Scrollable Main Content */}
          <main
            className="flex-1 overflow-y-auto w-full bg-transparent dendro-scrollbar"
            style={{
              paddingTop: `calc(var(--header-height, ${headerHeight}px) + 1.25rem)`,
              paddingLeft: '1.25rem',
              paddingRight: '1.25rem',
              // Exposé pour que les éléments collants (ex. pagination d'un tableau) se calent au bord réel de la zone.
              ['--main-pad-bottom' as string]: '3rem',
              paddingBottom: 'var(--main-pad-bottom)'
            }}
          >
            <div className="w-full max-w-none min-w-0 h-full">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
